"use client";

import { useI18n } from "@/i18n/I18nProvider";
import type { LexiconEntry } from "./entries";

/** The chart's size and its margins (left for the price scale, below for the bar numbers). */
const W = 560;
const H = 220;
const M = { l: 52, r: 18, t: 18, b: 34 };
const PW = W - M.l - M.r;
const PH = H - M.t - M.b;
const GRID_ROWS = 4;

/** The prices a chart shows: every candle's high and low, the path, the levels and zones; padded, room below for the volume. */
function priceRange(e: LexiconEntry): { min: number; max: number } {
  const band = e.band ? [...e.band.upper, ...e.band.lower] : [];
  const values = [...(e.c ?? []).flatMap((b) => [b[1], b[2]]), ...(e.line ?? []), ...(e.curves ?? []).flat(), ...band, ...(e.levels ?? []), ...(e.zones ?? []).flat()];
  let min = Math.min(...values);
  let max = Math.max(...values);
  const pad = (max - min) * 0.13 || 1;
  min -= pad;
  max += pad;
  if (e.vol) min -= (max - min) * 0.38;
  return { min, max };
}

/** A ribbon line's colour: from blue (the shortest, k 0) to red (the longest), as the chart's ribbon. */
const ribbonColour = (k: number, n: number) => `color-mix(in srgb, var(--bad) ${Math.round((100 * k) / Math.max(1, n - 1))}%, var(--accent))`;

/** The lines over the price path: averages in orange, a ribbon's lines blue to red (the long ones first, the short on top). */
function Curves({ e, x, y }: { e: LexiconEntry; x: (k: number) => number; y: (v: number) => number }) {
  const curves = e.curves ?? [];
  const colour = (k: number) => (e.ribbon ? ribbonColour(k, curves.length) : "var(--series-2)");
  const order = curves.map((_, k) => k).reverse();
  return order.map((k) => <polyline key={`m${k}`} points={curves[k].map((v, j) => `${x(j)},${y(v)}`).join(" ")} fill="none" style={{ stroke: colour(k) }} strokeWidth={e.ribbon ? 1.6 : 2.2} strokeLinejoin="round" />);
}

/** Bollinger's bands over the price path: the space between them faintly filled, the bands solid, the middle dashed. */
function Band({ e, x, y }: { e: LexiconEntry; x: (k: number) => number; y: (v: number) => number }) {
  if (!e.band) return null;
  const path = (vs: number[]) => vs.map((v, j) => `${x(j)},${y(v)}`);
  const { upper, mid, lower } = e.band;
  return (
    <g>
      <polygon points={[...path(upper), ...path(lower).reverse()].join(" ")} fill="var(--accent)" fillOpacity="0.08" />
      <polyline points={path(upper).join(" ")} fill="none" stroke="var(--accent)" strokeWidth="1.6" />
      <polyline points={path(lower).join(" ")} fill="none" stroke="var(--accent)" strokeWidth="1.6" />
      <polyline points={path(mid).join(" ")} fill="none" stroke="var(--muted)" strokeWidth="1.4" strokeDasharray="5 4" />
    </g>
  );
}

/**
 * A reference entry's schematic chart in the app's colours (light and dark): the price grid, its zones and levels, the
 * volume, the candles or the price path — with averages or bands over it the path in the text's colour —, and the
 * labels — as the cheat sheet draws it.
 */
export function LexiconChart({ e }: { e: LexiconEntry }) {
  const { t, f } = useI18n();
  if (!e.c && !e.line) return null;
  const { min, max } = priceRange(e);
  const y = (v: number) => M.t + ((max - v) / (max - min)) * PH;
  const n = e.c?.length ?? e.line!.length;
  const step = PW / n;
  const xBar = (k: number) => M.l + step * (k + 0.5);
  const xPath = (k: number) => M.l + (k * PW) / Math.max(1, n - 1);
  const xLabel = (k: number) => (e.c ? xBar(k) : xPath(k));
  const pathColour = e.curves || e.band ? "var(--text)" : "var(--accent)";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("lex.chart.aria", { name: t(`lex.${e.id}.name`) })} className="h-auto w-full rounded-lg border border-line bg-surface">
      {Array.from({ length: GRID_ROWS + 1 }, (_, k) => (
        <g key={k}>
          <line x1={M.l} y1={M.t + (k * PH) / GRID_ROWS} x2={W - M.r} y2={M.t + (k * PH) / GRID_ROWS} stroke="var(--grid)" />
          <text x={M.l - 8} y={M.t + (k * PH) / GRID_ROWS + 4} textAnchor="end" fontSize="10" fill="var(--muted)">
            {f.num(max - (k * (max - min)) / GRID_ROWS, 1)}
          </text>
        </g>
      ))}
      <line x1={M.l} y1={M.t} x2={M.l} y2={H - M.b} stroke="var(--axis)" strokeWidth="1.3" />
      <line x1={M.l} y1={H - M.b} x2={W - M.r} y2={H - M.b} stroke="var(--axis)" strokeWidth="1.3" />
      <text x="13" y="14" fontSize="10" fill="var(--muted)">{t("lex.chart.price")}</text>
      <text x={W - M.r} y={H - 7} textAnchor="end" fontSize="10" fill="var(--muted)">{t("lex.chart.time")}</text>
      {(e.zones ?? []).map(([low, high], k) => (
        <g key={`z${k}`}>
          <rect x={M.l} y={y(high)} width={PW} height={y(low) - y(high)} fill="var(--accent)" fillOpacity="0.16" stroke="var(--accent)" strokeDasharray="4 3" />
          <text x={M.l + 7} y={y(high) + 12} fontSize="10" fontWeight="700" fill="var(--accent-ink)">{t(`lex.${e.id}.zone.${k}`)}</text>
        </g>
      ))}
      {(e.levels ?? []).map((p, k) => (
        <g key={`l${k}`}>
          <line x1={M.l} y1={y(p)} x2={W - M.r} y2={y(p)} stroke="var(--warn)" strokeDasharray="5 4" />
          <text x={W - M.r - 3} y={y(p) - 4} textAnchor="end" fontSize="10" fill="var(--warn-ink)">{t(`lex.${e.id}.level.${k}`)}</text>
        </g>
      ))}
      <Band e={e} x={xPath} y={y} />
      <Curves e={e} x={xPath} y={y} />
      <Volume e={e} y0={H - M.b} step={step} />
      <Candles e={e} y={y} x={xBar} step={step} />
      {e.line && <polyline points={e.line.map((p, k) => `${xPath(k)},${y(p)}`).join(" ")} fill="none" stroke={pathColour} strokeWidth="2.8" strokeLinejoin="round" strokeLinecap="round" />}
      {e.line?.map((p, k) => <circle key={`p${k}`} cx={xPath(k)} cy={y(p)} r="3" fill={pathColour} />)}
      {(e.labels ?? []).map(([bar, price], k) => (
        <text key={`t${k}`} x={Math.min(W - M.r - 5, Math.max(M.l + 5, xLabel(bar)))} y={Math.max(M.t + 10, y(price) - 8)} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--text)" stroke="var(--surface)" strokeWidth="3" paintOrder="stroke">
          {t(`lex.${e.id}.label.${k}`)}
        </text>
      ))}
    </svg>
  );
}

/** The candles: wick and body, green when the close is at or above the open, red below; the bar numbers under the axis. */
function Candles({ e, y, x, step }: { e: LexiconEntry; y: (v: number) => number; x: (k: number) => number; step: number }) {
  const body = Math.min(24, step * 0.48);
  return (e.c ?? []).map(([o, h, l, c], k) => {
    const color = c >= o ? "var(--good)" : "var(--bad)";
    const top = y(Math.max(o, c));
    return (
      <g key={`c${k}`}>
        <line x1={x(k)} y1={y(h)} x2={x(k)} y2={y(l)} stroke={color} strokeWidth="2" />
        <rect x={x(k) - body / 2} y={top} width={body} height={Math.max(2, y(Math.min(o, c)) - top)} fill={color} rx="1" />
        <text x={x(k)} y={H - M.b + 14} textAnchor="middle" fontSize="9" fill="var(--muted)">{k + 1}</text>
      </g>
    );
  });
}

/** The volume bars at the foot (a quarter of the chart's height at the largest), in the candle's colour, faint. */
function Volume({ e, y0, step }: { e: LexiconEntry; y0: number; step: number }) {
  if (!e.vol || !e.c) return null;
  const top = Math.max(...e.vol);
  const body = Math.min(24, step * 0.48);
  const c = e.c;
  return e.vol.map((v, k) => {
    const h = (v / top) * PH * 0.24;
    return <rect key={`v${k}`} x={M.l + step * (k + 0.5) - body / 2} y={y0 - h} width={body} height={h} fill={c[k][3] >= c[k][0] ? "var(--good)" : "var(--bad)"} opacity="0.35" />;
  });
}
